/**
 * Bus de synchronisation cross-onglets.
 * ─────────────────────────────────────
 * Permet à un onglet de notifier instantanément les autres onglets ouverts
 * qu'une ressource (client, facture, devis…) a été modifiée → les listes
 * peuvent re-fetcher immédiatement sans attendre le prochain cycle de polling.
 *
 * Implémentation : BroadcastChannel (supporté sur tous les navigateurs modernes)
 * + fallback localStorage pour les anciens navigateurs.
 *
 * Usage côté écriture :
 *   import { broadcastDataChange } from '../lib/dataBus';
 *   await axios.patch(...);
 *   broadcastDataChange('client', clientId);   // notifie les autres onglets
 *
 * Usage côté lecture :
 *   import { subscribeDataChange } from '../lib/dataBus';
 *   useEffect(() => {
 *       const unsub = subscribeDataChange('client', () => fetchClients());
 *       return unsub;
 *   }, []);
 */

const CHANNEL_NAME = 'bigdem_data_bus_v1';
const STORAGE_KEY = 'bigdem_data_bus_event';

let _channel = null;
const _listeners = new Set();

function _getChannel() {
    if (_channel) return _channel;
    if (typeof window === 'undefined') return null;
    if (typeof BroadcastChannel === 'undefined') return null;
    try {
        _channel = new BroadcastChannel(CHANNEL_NAME);
        _channel.onmessage = (ev) => _dispatch(ev.data);
    } catch {
        _channel = null;
    }
    return _channel;
}

function _dispatch(event) {
    if (!event || !event.entity) return;
    _listeners.forEach((cb) => {
        try { cb(event); } catch { /* silencieux */ }
    });
}

// Fallback localStorage (déclenche `storage` event sur les autres onglets)
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
        if (e.key !== STORAGE_KEY || !e.newValue) return;
        try {
            const event = JSON.parse(e.newValue);
            _dispatch(event);
        } catch { /* ignore */ }
    });
    // Initialise le channel à l'import
    _getChannel();
}

/**
 * Notifie tous les autres onglets qu'une entité vient d'être modifiée.
 *
 * @param {string} entity      ex. 'client', 'facture', 'devis', 'prospect', 'visite'
 * @param {string} [resourceId] (optionnel) — id de la ressource modifiée
 * @param {object} [meta]       (optionnel) — métadonnées libres
 */
export function broadcastDataChange(entity, resourceId = null, meta = {}) {
    const event = {
        entity,
        resourceId,
        meta,
        ts: Date.now(),
        // origin permet d'éviter (côté listener) de réagir à ses propres broadcasts
        origin: typeof window !== 'undefined' ? (window.__bigdem_tab_id ||= Math.random().toString(36).slice(2)) : 'srv',
    };
    const ch = _getChannel();
    if (ch) {
        try { ch.postMessage(event); } catch { /* ignore */ }
    }
    // Fallback : localStorage event (notifie les autres onglets uniquement)
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(event));
        // Nettoyage rapide pour permettre un nouveau "déclenchement" même si la valeur ne change pas
        setTimeout(() => {
            try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        }, 100);
    } catch { /* ignore — quota / mode privé */ }
}

/**
 * S'abonne aux changements d'une entité (provenant d'un AUTRE onglet).
 * Le callback NE sera PAS déclenché par les broadcasts de l'onglet courant.
 *
 * @param {string|string[]} entities  Entité(s) à écouter ('client' ou ['client','facture'])
 * @param {function}        cb        Appelé avec l'événement `{entity, resourceId, meta, ts}`
 * @returns {function} unsubscribe
 */
export function subscribeDataChange(entities, cb) {
    const list = Array.isArray(entities) ? entities : [entities];
    const myOrigin = typeof window !== 'undefined' ? (window.__bigdem_tab_id ||= Math.random().toString(36).slice(2)) : 'srv';
    const wrapper = (event) => {
        if (event.origin === myOrigin) return; // ignore self
        if (!list.includes(event.entity)) return;
        cb(event);
    };
    _listeners.add(wrapper);
    return () => _listeners.delete(wrapper);
}
